import { useEffect, useRef } from 'react';
import { Locales, localeLabel } from '../../core/locale';
import { Locale } from '../../core/locale/types';
import Logo from '../Logo/Logo';
import './Header.css';
import { type DropDownOption } from '../DropDown/DropDownOptions';
import Caret from '../common/caret.svg';
import DropDown from '../DropDown/DropDown';
import { useLanguageContext } from '../../core/locale/LanguageContext';
import { useAppContext } from '../../core/context';
import { useApi } from '../../platform/api/useApi';
import { useIframe } from '../../platform/iframe/useIframe';
import { useFlag } from '../../feature-flags';

const parentMessageSource = 'vipps-widget-sdk-parent';
const parentRequestCancelEvent = 'request-cancel';
const iframeCancelEvent = 'cancel';

function Header(): React.ReactNode {
  const context = useAppContext();
  const showNewDesign = useFlag('redesign');
  const apis = useApi();
  const iframe = useIframe();
  const languageContext = useLanguageContext();
  const isCancellingRef = useRef(false);
  const isEmbed = iframe.isEmbedded();

  const onClickLocale = (l: Locale) => (event: React.UIEvent) => {
    event.stopPropagation();
    languageContext.setLocale(l);
  };

  useEffect(() => {
    if (!isEmbed) {
      return;
    }

    const onParentMessage = (event: MessageEvent) => {
      if (
        event.data?.source !== parentMessageSource ||
        event.data?.event !== parentRequestCancelEvent
      ) {
        return;
      }

      if (isCancellingRef.current) {
        return;
      }
      isCancellingRef.current = true;

      void (async () => {
        try {
          await apis.cancel(context);
        } finally {
          iframe.postToParent({ event: iframeCancelEvent });
          isCancellingRef.current = false;
        }
      })();
    };

    window.addEventListener('message', onParentMessage);
    return () => window.removeEventListener('message', onParentMessage);
  }, [context, isEmbed, apis, iframe]);

  const options: DropDownOption[] = Locales.map((l) => ({
    element: <span>{localeLabel(l, languageContext.countryLocalization).short}</span>,
    id: l,
    className: 'locale',
    onClick: onClickLocale(l),
    ariaLabel: localeLabel(l, languageContext.countryLocalization).long,
    name: localeLabel(l, languageContext.countryLocalization).short,
  }));

  if (isEmbed) {
    return null;
  }

  return (
    <div className="header">
      <Logo />
      {!showNewDesign && (
        <div className="locale-switcher">
          <DropDown
            options={options}
            selector={
              <>
                <span>
                  {localeLabel(languageContext.locale, languageContext.countryLocalization).short}
                </span>
                <img className="caret" alt="Caret" aria-hidden="true" src={Caret} />
              </>
            }
            optionsClassName="locale-picker"
            selectorId="locale-switcher"
            selectedId={languageContext.locale}
            ariaLabel={languageContext.getLocalizedText(
              'localeSelectorLabel',
              localeLabel(languageContext.locale, languageContext.countryLocalization).long
            )}
          />
        </div>
      )}
    </div>
  );
}

export default Header;
